
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, Plus, Images } from "lucide-react";
import ImageUpload from "./ImageUpload";

interface GalleryUploadProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
}

const GalleryUpload = ({ images, onImagesChange }: GalleryUploadProps) => {
  const [showUpload, setShowUpload] = useState(false);

  const addImage = (url: string) => {
    onImagesChange([...images, url]);
    setShowUpload(false);
  };

  const removeImage = (index: number) => {
    const updated = images.filter((_, i) => i !== index);
    onImagesChange(updated);
  };

  const moveImage = (fromIndex: number, toIndex: number) => {
    const updated = [...images];
    const [movedImage] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, movedImage);
    onImagesChange(updated);
  };

  return (
    <Card className="admin-card border-admin-border">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-admin-foreground flex items-center gap-2">
            <Images className="h-5 w-5" />
            Product Gallery
          </CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowUpload(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Image
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showUpload && (
          <div className="border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-admin-foreground">Upload New Image</h4>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowUpload(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <ImageUpload
              onImageUploaded={addImage}
              folder="products/gallery"
            />
          </div>
        )}

        {images.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {images.map((imageUrl, index) => (
              <div key={index} className="relative group">
                <img
                  src={imageUrl}
                  alt={`Product image ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg border"
                />
                <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-2">
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => removeImage(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  {index > 0 && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => moveImage(index, index - 1)}
                    >
                      ←
                    </Button>
                  )}
                  {index < images.length - 1 && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => moveImage(index, index + 1)}
                    >
                      →
                    </Button>
                  )}
                </div>
                <div className="absolute top-2 left-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                  {index + 1}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-admin-muted-foreground">
            <Images className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-admin-foreground">No images added yet</p>
            <p className="text-sm">Click "Add Image" to upload product gallery images</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GalleryUpload;
